import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { TableContainer, Paper, Typography, Box } from "@mui/material";
import { api, get } from "../../../../utils/api";
import PaginationBox from "../../../../Components/Masters/Pagination";
import DeleteAlertDialog from "../../../../Components/Masters/DeleteAlertDialog";
import ColumnHeadings from "../../../../Components/Masters/ColumnHeadings";
import MenuActions from "../../../../Components/Masters/MenuActions";
import TableContent from "../../../../Components/Masters/TableContent";
import FilterDrawer from "../../../../Components/Masters/FilterDrawer";
import MessagePopup from "../../../../Components/Masters/MessagePopup";
import TableHeading from "../../../../Components/Masters/TableHeading";
import MenuIcon from "../../../../Components/Masters/MenuIcon";
import { companyId } from "../../../../utils/companyId";
import Add from "./Add";

import DateRangePickerComponent from "../../../Masters/Roles and Permissions/Filters/Date";
import MultiSelect from "../../../Masters/Roles and Permissions/Filters/MultiSelect";
import Cards from "../../../../Components/Common/Cards";
import NoDataFound from "../../../../Components/Masters/NoDataFound";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import { getMonthName } from "../../../../utils/getMonthName";
// import MonthYearFilter from "../../../Masters/Roles and Permissions/Filters/MonthYearFilter";
import OffsetBulkUpload, { OffsetBulkUploadModal } from "./BulkUpload";
import { fetchOffsetCreditVendorOptions, fetchOffsetCreditProjectTypeOptions } from "./offsetBulkUploadUtils";
import Unauthorized from "../../../../Components/Unauthorized/Unauthorized";
import TopMenuUpload from "../../../../Components/Masters/TopMenuUpload";
import { truncateNumber } from "../../../../utils/common";
import Scope4Layout from "../../../../Layout/Scope4Layout";
import View from "../InHouseOffset/InHouse/View";
import { useUser } from "../../../../context/AuthContext";
import {FacilityColumnHeading,
  facilityDataCellSx,
  getFacilityName,
  GRID_TABLE_ACTION_COL,
  getGridTableRowSx,
  gridTableActionHeaderSx,
  gridTablePaperSx,
  isFacilityHeading,
  shouldShowFacilityColumn,
  withFacilityHeading, FacilityDataCell, MasterDateDataCell } from "../../../../utils/facilityColumn";


/** Same lists as Add form â€” used for bulk template + download reference sheets. */
const OFFSET_APPLIED_SCOPES_OPTIONS = [
  "Scope 1",
  "Scope 2",
  "Scope 3 - Category 1",
  "Scope 3 - Category 3",
  "Scope 3 - Category 5",
  "Scope 3 - Category 6",
  "Scope 3 - Category 7",
  "Scope 3 - Category 9",
];

const OFFSET_VENDOR_OPTIONS = [
  "Verra (VCS)",
  "Gold Standard",
  "Climate Action Reserve (CAR)",
  "American Carbon Registry (ACR)",
  "Plan Vivo",
  "CDM (Clean Development Mechanism)",
  "Puro.Earth",
  "ART-TREES (Architecture for REDD+ Transactions)",
  "BioCarbon Registry",
  "India GHG Program",
  "Nori",
  "South Pole",
  "ClimeCo",
  "CarbonClear",
 "Other specified",
];

const OFFSET_PROJECT_TYPE_OPTIONS = [
  "REDD+ (Forestry / Avoided Deforestation)",
  "Afforestation / Reforestation",
  "Solar Energy Projects",
  "Wind Energy Projects",
  "Hydropower Projects",
  "Biogas / Biomethane Projects",
  "Landfill Gas Recovery",
  "Energy Efficiency (Industrial or Residential)",
  "Cookstove Distribution Projects",
  "Waste-to-Energy",
  "Biochar Production",
  "Direct Air Capture (DAC)",
  "Soil Carbon Sequestration",
  "Blue Carbon (Mangroves, Seagrass)",
  "Carbon Mineralization / Enhanced Weathering",
  "Carbon Capture and Storage (CCS)",
  "Agricultural Methane Reduction",
  "Other specified"
];

const OFFSET_MONTH_COL = "Month (in 01/25 or Jan-25)";
const OFFSET_CREDITS_COL = "No. of credits purchased (tCO2e)";
const OFFSET_APPLIED_SCOPES_COL =
  "Applied to which scope(s) / emission source(s)";

function getMergedProjectTypes(uniqueValues = {}, masterProjectTypes = OFFSET_PROJECT_TYPE_OPTIONS) {
  const api = (uniqueValues.projectType || []).filter(Boolean);
  const seen = new Set(masterProjectTypes);
  const rest = api.filter((p) => !seen.has(p));
  return [...masterProjectTypes, ...rest];
}

function getMergedVendors(uniqueValues = {}, masterVendors = OFFSET_VENDOR_OPTIONS) {
  const api = (uniqueValues.vendorRegistry || []).filter(Boolean);
  const seen = new Set(masterVendors);
  const rest = api.filter((v) => !seen.has(v));
  return [...masterVendors, ...rest];
}

/** Template rows for bulk download â€” aligned with Add form fields. */
const getBulkDropdownOptions = (values = []) =>
  (values || []).filter(
    (value) => String(value).trim().toLowerCase() !== "other specified",
  );
function createOffsetBulkTemplateRow(
  serialNo,
  vendorPreset = "",
  projectTypePreset = "",
  vintageYearPreset = "",
  appliedScopesPreset = "",
) {
  return {
    "S No.": serialNo,
    [OFFSET_MONTH_COL]: "",
    Vendor: vendorPreset,
    "Project ID/ Name": "",
    "Project Type": projectTypePreset,
    [OFFSET_CREDITS_COL]: "",
    "Vintage Year": vintageYearPreset,
    [OFFSET_APPLIED_SCOPES_COL]: appliedScopesPreset,
    "Remark (optional)": "",
  };
}

function getDefaultOffsetBulkExcelData(
  vendors = OFFSET_VENDOR_OPTIONS,
  projectTypes = OFFSET_PROJECT_TYPE_OPTIONS,
) {
  const appliedScopes = OFFSET_APPLIED_SCOPES_OPTIONS;
  const n = Math.max(
    vendors.length,
    projectTypes.length,
    appliedScopes.length,
  );
  return Array.from({ length: n }, (_, i) =>
    createOffsetBulkTemplateRow(
      i + 1,
      vendors[i % vendors.length],
      projectTypes[i % projectTypes.length],
      "",
      appliedScopes[i % appliedScopes.length],
    ),
  );
}

/** Multi-sheet payload for `generateExcel`: template + option lookup sheets. */
function buildOffsetExcelDownloadSheets({
  vendors = OFFSET_VENDOR_OPTIONS,
  projectTypes = OFFSET_PROJECT_TYPE_OPTIONS,
} = {}) {
  const main = getDefaultOffsetBulkExcelData(vendors, projectTypes);

  const appliedScopes = OFFSET_APPLIED_SCOPES_OPTIONS;

  const maxOptionRows = Math.max(
    vendors.length,
    projectTypes.length,
    appliedScopes.length,
  );
  const dropdownOptionsRows = Array.from({ length: maxOptionRows }, (_, i) => ({
    "S No.": i + 1,
    Vendor: vendors[i % vendors.length],
    "Project Type": projectTypes[i % projectTypes.length],
    [OFFSET_APPLIED_SCOPES_COL]: appliedScopes[i % appliedScopes.length],
  }));

  return [
    { name: "Template", data: main },
    {
      name: "Valid dropdown options",
      data: dropdownOptionsRows,
    },
    {
      name: "Vendor options",
      data: vendors.map((option, i) => ({
        "S No.": i + 1,
        Vendor: option,
      })),
    },
    {
      name: "Project Type options",
      data: projectTypes.map((option, i) => ({
        "S No.": i + 1,
        "Project Type": option,
      })),
    },
  ];
}

const Offset = () => {
  const { user } = useUser();
  const showFacilityColumn = shouldShowFacilityColumn(user);
  const columnWidth = showFacilityColumn
    ? `minmax(0, 0.9fr) minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1.5fr) ${GRID_TABLE_ACTION_COL}`
    : `minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 1.3fr) minmax(0, 1.2fr) minmax(0, 1.6fr) ${GRID_TABLE_ACTION_COL}`;
  // const [selectedDate, setSelectedDate] = useState({});
  const [cardData, setCardData] = useState(null);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const tableContainerRef = useRef(null); // Reference to the table container
  const [isShortContent, setIsShortContent] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addData, setAddData] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    Date: false,
    // "Month of energy export": false,
    Vendor: false,
    "Project Type": false,
  });
  const [viewData, setViewData] = useState(false);
  const [element, setElement] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  // const [limit] = useState(10); // Number of items per page
  const [selectedFilter, setSelectedFilter] = useState("Date");
  const [drawerFilterOpen, setDrawerFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rowsPerPage] = useState(10);
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [selectedMonth, setSelectedMonth] = useState(""); // Stores the selected month
  const [selectedYear, setSelectedYear] = useState(""); // Stores the selected year
  const [uniqueValues, setUniqueValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [selectedFields, setSelectedFields] = useState({
    projectType: [],
    vendorRegistry: [],
  });
  // const [quantityRange, setQuantityRange] = useState({ min: "", max: "" });
  // const [hoursRange, setHoursRange] = useState({ min: "", max: "" });
  const [startMonth, setStartMonth] = useState(null);
  const [endMonth, setEndMonth] = useState(null);
  const [jsonBulkUpload, setJsonBulkUpload] = useState([]);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [bulkUploadTable, setBulkUploadTable] = useState(false);
   const [matchData, setMatchData] = useState([]);
  const [formDataBulkUpload, setFormDataBulkUpload] = useState({});
  const [bulkUploadFieldErrors, setBulkUploadFieldErrors] = useState({});
  const [bulkUploadAutoShowInput, setBulkUploadAutoShowInput] = useState({});
  const [bulkMasterOptions, setBulkMasterOptions] = useState({
    vendors: OFFSET_VENDOR_OPTIONS,
    projectTypes: OFFSET_PROJECT_TYPE_OPTIONS,
  });
  const excelDataForBulkModal = useMemo(
    () => buildOffsetExcelDownloadSheets(bulkMasterOptions),
    [bulkMasterOptions],
  );

  useEffect(() => {
    const loadBulkMasterOptions = async () => {
      try {
        const [vendors, projectTypes] = await Promise.all([
          fetchOffsetCreditVendorOptions(OFFSET_VENDOR_OPTIONS),
          fetchOffsetCreditProjectTypeOptions(OFFSET_PROJECT_TYPE_OPTIONS),
        ]);
        setBulkMasterOptions({ vendors, projectTypes });
      } catch (error) {
        console.error("Error fetching offset bulk master options:", error);
      }
    };
    loadBulkMasterOptions();
  }, []);


  const [tempFilter, setTempFilter] = useState({
    startDate: null,
    endDate: null,
    startMonth: null,
    endMonth: null,
    projectType: [],
    vendorRegistry: [],
  });

  // const [equipmentDetails, setEquipmentDetails] = useState({});
  // const [formData, setFormData] = useState({});

  let bannerData = [
    {
      title: "Total Credits Purchased",
      description: truncateNumber(cardData?.total),
      // unit: cardData?.total?.unit,
    },
    {
      title: "Top Project Type By No. of credits purchased",
      description:
        cardData?.topProjectTypes?.length > 0
          ? cardData?.topProjectTypes.map((el) => el.projectType)
          : "N/A",
    },
    {
      title: "Energy export Month",
      description: truncateNumber(cardData?.monthly?.totalCreditsPurchased),
      // unit: cardData?.monthly?.unit || "",
      filter: "date",
      defaultValue: {},
    },
  ];

  const fetchCardData = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth(); // Current month (0-based, January = 0)
      const response = await get(
        `scope4/off-set-credit-purchase/cardData?year=${
          selectedYear ? selectedYear : currentYear
        }&month=${selectedMonth ? selectedMonth : currentMonth + 1}`
      );
      setCardData(response.data);
    } catch (error) {
      console.error("Error fetching card data:", error);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchCardData();
  }, [selectedMonth, selectedYear]);

  const handlePeriodChange = (key, value, filterType) => {
    const [year, month] = value.split("-");
    setSelectedMonth(month);
    setSelectedYear(year);
    fetchCardData(year, month, "");
  };

  const filters = ["Date", "Project Type", "Vendor"];
  const options = useMemo(() => [...OFFSET_APPLIED_SCOPES_OPTIONS], []);
  const inputData = useMemo(() => ([
    {
      fieldName: "consumptionPeriod",
      title: "Month of energy export",
      required: true,
      type: "date",
    },
    {
      fieldName: "vendorRegistry",
      title: "Vendor",
      type: "select",
      options: [...OFFSET_VENDOR_OPTIONS],
    },
    {
      fieldName: "projectIdOrName",
      title: "Project ID/ Name",
      type: "text",
    },
    {
      fieldName: "projectType",
      title: "Project Type",
      type: "select",
      options: [...OFFSET_PROJECT_TYPE_OPTIONS],
    },
    {
      fieldName: "creditsPurchased",
      title: "No. of credits purchased (tCO₂e)",
      type: "number",
    },
    {
      fieldName: "vintageYear",
      title: "Vintage Year",
      type: "text",
      placeholder: "e.g. 2020",
    },
    {
      fieldName: "appliedScopes",
      title: "Applied to which scope(s) / emission source(s)",
      type: "select",
      options: options,
    },
    {
      fieldName: "remarks",
      title: "Remark (optional)",
      placeholder: "Additional remarks",
      type: "text",
    },
    {
      fieldName: "editRemarkReason",
      title: "Edit Remark Reason",
      placeholder: "Additional edit remarks",
      type: "text",
      condition: true,
    },
    {
      fieldName: "documents",
      title: "Upload Documents (optional)",
      type: "file",
    },
  ]), [options]);

  // Merge static options with newly added unique values for dropdowns
  const computedInputData = useMemo(() => {
    return inputData.map((field) => {
      if (field.fieldName === "vendorRegistry") {
        return { ...field, options: getMergedVendors(uniqueValues, bulkMasterOptions.vendors) };
      }
      if (field.fieldName === "projectType") {
        return { ...field, options: getMergedProjectTypes(uniqueValues, bulkMasterOptions.projectTypes) };
      }
      return field;
    });
  }, [uniqueValues, inputData, bulkMasterOptions]);

  const fetchUniqueValues = async () => {
    try {
      const response = await get(`scope4/off-set-credit-purchase/get`);
      const data = response.data.data;
      const uniqueProjectType = [
        ...new Set(
          data.map((el) => el.projectType).filter(Boolean) // Filter out undefined values
        ),
      ];
      const uniqueVendor = [
        ...new Set(
          data.map((el) => el.vendorRegistry).filter(Boolean) // Filter out undefined values
        ),
      ];

      const uniqueVintageYear = [
        ...new Set(data.map((el) => el.vintageYear).filter(Boolean)),
      ];

      const appliedScopeOptions = new Set();
      data.forEach((el) => {
        const a = el?.appliedScopes;
        if (Array.isArray(a)) {
          a.forEach((s) => {
            if (s != null && String(s).trim() !== "")
              appliedScopeOptions.add(String(s).trim());
          });
        } else if (typeof a === "string" && a.trim()) {
          a.split(",").forEach((p) => {
            const t = p.trim();
            if (t) appliedScopeOptions.add(t);
          });
        }
      });

      return {
        projectType: uniqueProjectType,
        vendorRegistry: uniqueVendor,
        vintageYear: uniqueVintageYear,
        appliedScopeOptions: [...appliedScopeOptions],
      };
    } catch (error) {
      console.error("Error fetching unique values:", error);
      return {};
    }
  };

  // useEffect(()=>{
  //   fetchCardData();
  // })

 

  const fetchData = async (
    filters = isFiltersApplied ? filtersToApply : {},
    pageNumber = page
  ) => {
    fetchCardData(selectedMonth, selectedMonth);
    try {
      const queryParams = new URLSearchParams({
        ...filters,
        page:pageNumber ? pageNumber : page, // Add the current page
        limit: rowsPerPage, // Add the number of rows per page
        sortField: sortConfig.key, // Sort configuration
        sortOrder: sortConfig.direction,
        company: companyId,
        createdBy: companyId,
      }).toString();
      const response = await get(
        `/scope4/off-set-credit-purchase/get?${queryParams}`
      );
      setData(response.data.data); // Update the user list
      setTotalPages(response.data.totalPages); // Update total pages for pagination
      const uniqueValues = await fetchUniqueValues();
      setUniqueValues(uniqueValues);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const updatePage = () => {
    if (page > 1 && data.length === 1) {
      setPage(page - 1);
    }
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, sortConfig]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const tableHeight = container.scrollHeight; // Get the table's height
      const screenHeight = window.innerHeight; // Get the viewport height

      // If table content is less than half the screen height, set pagination to be at the bottom
      setIsShortContent(tableHeight < screenHeight / 3);
    }
  }, [data]);

  const handleMenuClick = (event, element) => {
    setAnchorEl(event.currentTarget);
    setSelectedData(element);
    setElement(element);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedData(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setAddData(true);
    setViewData(false);
  };

  const handleView = () => {
    handleMenuClose();
    setViewData(true);
    setAddData(false);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

 
  // Applying selected filters
  const handleApplyFilters = () => {
    setPage(1);
    setStartDate(tempFilter?.startDate);
    setEndDate(tempFilter?.endDate);
    setSelectedFields({
      projectType: tempFilter?.projectType || [],
      vendorRegistry: tempFilter?.vendorRegistry || [],
    });
    setStartMonth(tempFilter?.startMonth);
    setEndMonth(tempFilter?.endMonth);
    fetchData(filtersToApply,1);
    handleAppliedFilters();
    setDrawerFilterOpen(false);
  };


  const filtersToApply = {
    startDate: tempFilter?.startDate
      ? tempFilter?.startDate.format("YYYY-MM-DD")
      : "",
    endDate: tempFilter?.endDate
      ? tempFilter?.endDate.format("YYYY-MM-DD")
      : "",
    startMonth: tempFilter?.startMonth
      ? tempFilter?.startMonth.format("YYYY-MM")
      : "",
    endMonth: endMonth ? endMonth.format("YYYY-MM") : "",
    projectType: tempFilter?.projectType || [],
    vendorRegistry: tempFilter?.vendorRegistry || [],
  };

  const handleResetFilters = async () => {
    // Reset local state for date selection and selected fields
    setTempFilter({
      startDate: null,
      endDate: null,
      startMonth: null,
      endMonth: null,
      projectType: [],
      vendorRegistry: [],
    });
    setStartDate(null);
    setEndDate(null);
    setStartMonth(null);
    setEndMonth(null);
    setSelectedFields({
      projectType: [],
      vendorRegistry: [],
    });

    // Call fetchData without any filters
    await fetchData({});
    setErrors({});
    setAppliedFilters({});
    setIsFiltersApplied(false);
    setDrawerFilterOpen(false);
  };

  const handleAppliedFilters = () => {
    const isDateApplied = tempFilter?.startDate || tempFilter?.endDate;
    const isMonthApplied = tempFilter?.startMonth || tempFilter?.endMonth;
    const isProjectTypeApplied = tempFilter?.projectType?.length > 0;
    const isVendorApplied = tempFilter?.vendorRegistry?.length > 0;
    setIsFiltersApplied(
      isDateApplied || isMonthApplied || isProjectTypeApplied || isVendorApplied
    );

    setAppliedFilters((prevAppliedFilters) => ({
      ...prevAppliedFilters,
      Date: isDateApplied,
      // "Month of energy export": isMonthApplied,
      "Project Type": isProjectTypeApplied,
      Vendor: isVendorApplied,
    }));
  };

  // Handle Sorting function
  const handleSort = (column, direction) => {
    setSortConfig({ key: column, direction });
  };

  const handleFilterDrawer = () => {
    setTempFilter({
      startDate: startDate,
      endDate: endDate,
      startMonth: startMonth,
      endMonth: endMonth,
      projectType: selectedFields.projectType,
      vendorRegistry: selectedFields.vendorRegistry,
    });
    setDrawerFilterOpen(false);
  };

  const columnHeadings = withFacilityHeading(
    [
      { title: "Date", fieldName: "createdAt" },
      {
        fieldName: "consumptionPeriod",
        title: "Month of energy export",
      },
      {
        fieldName: "vendorRegistry",
        title: "Vendor",
      },
      {
        fieldName: "projectType",
        title: "Project Type",
      },
      {
        fieldName: "creditsPurchased",
        title: "No. of credits purchased (tCO₂e)",
      },
    ],
    showFacilityColumn
  );

  const handleCloseBulkUpload = () => {
    setBulkUploadModal(false);
    fetchData();
  };

  const onConfirm = async () => {
    handleCloseBulkUploadReview();
    fetchData();
  };

  // Function to render the selected filter component based on user's choice
  const renderSelectedFilter = () => {
    switch (selectedFilter) {
      case "Date":
        return (
          <DateRangePickerComponent
            startDate={tempFilter?.startDate}
            endDate={tempFilter?.endDate}
            setStartDate={(date) =>
              setTempFilter((prev) => ({ ...prev, startDate: date }))
            }
            setEndDate={(date) =>
              setTempFilter((prev) => ({ ...prev, endDate: date }))
            }
            setErrors={setErrors}
          />
        );
      // case "Month of energy export":
      //   return (
      //     <MonthYearFilter
      //       startMonth={tempFilter?.startMonth}
      //       endMonth={tempFilter?.endMonth}
      //       setStartMonth={(month) =>
      //         setTempFilter((prev) => ({ ...prev, startMonth: month }))
      //       }
      //       setEndMonth={(month) => {
      //         setTempFilter((prev) => ({ ...prev, endMonth: month }));
      //       }}
      //       setErrors={setErrors}
      //       errors={errors.Month}
      //     />
      //   );
      case "Project Type":
        return (
          <MultiSelect
            title="Project Type"
            selectedFields={tempFilter}
            setSelectedFields={(updater) => {
              setTempFilter((prev) => {
                const newValue =
                  typeof updater === "function" ? updater(prev) : updater;
                return {
                  ...prev,
                  projectType: newValue.projectType || newValue,
                };
              });
            }}
            setErrors={setErrors}
            field="projectType"
            data={uniqueValues.projectType}
          />
        );
      case "Vendor":
        return (
          <MultiSelect
            title="Vendor"
            selectedFields={tempFilter}
            setSelectedFields={(updater) => {
              setTempFilter((prev) => {
                const newValue =
                  typeof updater === "function" ? updater(prev) : updater;
                return {
                  ...prev,
                  vendorRegistry: newValue.vendorRegistry || newValue,
                };
              });
            }}
            setErrors={setErrors}
            field="vendorRegistry"
            data={uniqueValues.vendorRegistry}
          />
        );
      default:
        return null;
    }
  };
  const uploadErrorClose = (_fromError, normalized, reviewMeta = {}) => {
    const reviewRows = Array.isArray(reviewMeta?.rows) ? reviewMeta.rows : [];
    if (reviewRows.length > 0) {
      setMatchData(reviewRows);
      setFormDataBulkUpload(reviewRows);
      setJsonBulkUpload(reviewRows);
    }
    if (reviewMeta?.fieldErrors) {
      setBulkUploadFieldErrors(reviewMeta.fieldErrors);
    }
    if (reviewMeta?.autoShowInput) {
      setBulkUploadAutoShowInput(reviewMeta.autoShowInput);
    }
    setBulkUploadTable(true);

    fetchUniqueValues()
      .then((values) => {
        if (values) setUniqueValues(values);
      })
      .catch((error) => {
        console.error("Error fetching values for bulk upload review:", error);
      });
  };

  const handleCloseBulkUploadReview = () => {
    setBulkUploadTable(false);
    setBulkUploadFieldErrors({});
    setBulkUploadAutoShowInput({});
  };

  return (
    <MainDashboardLayout sidebar={<Scope4Layout />}>
      <Unauthorized requiredModule="Environment | Scope 4 | Carbon Offsetting | OffsetCredit">
        <Box
          sx={{
            backgroundColor: "#ededed",
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <MessagePopup {...{ snackOpen, setsnackOpen }} />{" "}
          {/*Message popup for success or failure */}
          {/* View log, Edit, delete menus */}
          <Add
            id={element ? element._id : ""}
            addData={addData}
            setAddData={setAddData}
            data={element}
            title="Data"
            addUrl={`/scope4/off-set-credit-purchase/add`}
            updateUrl={`/scope4/off-set-credit-purchase/update`}
            fetchData={fetchData}
            setsnackOpen={setsnackOpen}
            inputData={computedInputData}
          />
          <View
            id={element ? element._id : ""}
            data={element}
            title={"Offset Credit Purchase Tracking"}
            viewData={viewData}
            setViewData={setViewData}
            inputData={computedInputData}
            handleEdit={handleEdit}
          />
          <Cards
            data={bannerData}
            title={"Carbon Offsetting"}
            onFilterChange={handlePeriodChange}
            {...{
              setSelectedMonth,
              setSelectedYear,
              selectedMonth,
              selectedYear,
            }}
          />
          {deleteDialog && (
            <DeleteAlertDialog
              fetchData={updatePage}
              {...{
                deleteDialog,
                setDeleteDialog,
                id: element._id,
                method:"delete",
                url: `${api}/scope4/off-set-credit-purchase/delte`,
                setsnackOpen,
                fetchData,
                fetchCardData,
              }}
            />
          )}
          {/* Filter Drawer */}
          {/* <Toolbar sx={{ padding: "0 16px", marginBottom: "20px" }}> */}
          {drawerFilterOpen && (
            <FilterDrawer
              {...{
                renderSelectedFilter,
                drawerFilterOpen,
                setDrawerFilterOpen,
                handleApplyFilters,
                handleFilterDrawer,
                setSelectedFilter,
                handleResetFilters,
                selectedFilter,
                filters,
                appliedFilters,
                selectedFields,
                setSelectedFields,
              }}
            />
          )}{" "}
          {/* Top Box for Heading, Filter Button, Add Data Button */}
          <TopMenuUpload
            setData={setElement}
            setAddData={setAddData}
            {...{
              setBulkUploadModal,
              setDrawerFilterOpen,
              filterApplied: isFiltersApplied,
            }}
          />
          {/* </Toolbar> */}
          <TableContainer
            component={Paper}
            ref={tableContainerRef}
            sx={{ ...gridTablePaperSx, overflowX: "hidden" }}
          >
            <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
              <TableHeading heading={"Offset Credit Purchase Tracking"} />
              <Box
                sx={{
                  ...getGridTableRowSx(columnWidth, showFacilityColumn),
                  borderBottom: "1px solid #ddd",
                  borderTop: "1px solid #ddd",
                }}
              >
                {columnHeadings.map((el) =>
                  isFacilityHeading(el) ? (
                    <FacilityColumnHeading
                      key={el.fieldName}
                      handleSort={handleSort}
                      sortConfig={sortConfig}
                    />
                  ) : (
                    <ColumnHeadings
                      key={el.fieldName}
                      {...{
                        title: el.title,
                        key: el.title,
                        handleSort,
                        field: el.fieldName,
                        sortConfig,
                      }}
                    />
                  )
                )}
                <Typography sx={gridTableActionHeaderSx}>
                  Action
                </Typography>
              </Box>
              {data.length === 0 ? (
                <NoDataFound />
              ) : (
                data.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      ...getGridTableRowSx(columnWidth, showFacilityColumn),
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    {columnHeadings.map((el) =>
                      isFacilityHeading(el) ? (
                        <FacilityDataCell key={el.fieldName}>
                          {getFacilityName(item) || "N/A"}
                        </FacilityDataCell>
                      ) : (
                        <TableContent
                          key={el.fieldName}
                          {...{
                            fieldData:
                              el.fieldName === "consumptionPeriod"
                                ? getMonthName(item[el.fieldName]?.month) +
                                  " " +
                                  item[el.fieldName]?.year
                                : el?.fieldName === "projectLocation"
                                ? item?.projectLocation?.city +
                                  ", " +
                                  item?.projectLocation?.state +
                                  ", " +
                                  item?.projectLocation?.country
                                : el.fieldName === "quantityConsumed"
                                ? truncateNumber(item[el.fieldName].value) +
                                  " " +
                                  item[el.fieldName].unit
                                : typeof item[el.fieldName] === "object" &&
                                  item[el.fieldName] !== null &&
                                  item[el.fieldName].industryProcess
                                ? item[el.fieldName].industryProcess
                                : typeof item[el.fieldName] === "object" &&
                                  item[el.fieldName] !== null &&
                                  item[el.fieldName].name
                                ? item[el.fieldName].name
                                : item[el.fieldName],
                            dateField:
                              el.fieldName === "createdAt"
                                ? item.createdAt
                                : false,
                          }}
                        />
                      )
                    )}
                    {/* Menu Action Icon */}
                    <MenuIcon {...{ handleMenuClick, field: item }} />
                  </Box>
                ))
              )}
            </Box>
          </TableContainer>
          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationBox
              isShortContent={isShortContent}
              {...{ totalPages, page, handleChangePage }}
            />
          )}
          <MenuActions
            {...{
              id: element?._id,
              handleView,
              handleEdit,
              handleDelete,
              anchorEl,
              handleMenuClose,
            }}
          />
          {/*Add,Edit,Delete Icons & function calls */}
        </Box>

        {bulkUploadModal && (
          <OffsetBulkUploadModal
            onCloseBulkUpload={handleCloseBulkUpload}
            isBulkUploadModal={bulkUploadModal}
            setsnackOpen={setsnackOpen}
            excelData={excelDataForBulkModal}
            setFormDataBulkUpload={setFormDataBulkUpload}
            formDataBulkUpload={formDataBulkUpload}
            setMatchData={setMatchData}
            setJsonBulkUpload={setJsonBulkUpload}
            errorClose={uploadErrorClose}
            setBulkUploadTable={setBulkUploadTable}
            setBulkUploadModal={setBulkUploadModal}
            matchedKey={"statusList"}
            fileName={"Offset"}
            url={"/scope4/off-set-credit-purchase/bulkupload"}
          />
        )}
        {bulkUploadTable && (
          <OffsetBulkUpload
            matchData={matchData}
            setMatchData={setMatchData}
            onClose={handleCloseBulkUploadReview}
            bulkUploadTable={bulkUploadTable}
            jsonBulkUpload={jsonBulkUpload}
            formDataBulkUpload={formDataBulkUpload}
            setFormDataBulkUpload={setFormDataBulkUpload}
            initialFieldErrors={bulkUploadFieldErrors}
            initialShowCustomInput={bulkUploadAutoShowInput}
            onReviewError={(reviewMeta) => {
              if (reviewMeta?.fieldErrors) {
                setBulkUploadFieldErrors(reviewMeta.fieldErrors);
              }
              if (reviewMeta?.autoShowInput) {
                setBulkUploadAutoShowInput(reviewMeta.autoShowInput);
              }
            }}
            title1="Vendor"
            title2="Project Type"
            title3={OFFSET_APPLIED_SCOPES_COL}
            keyValue1="Vendor"
            keyValue2="Project Type"
            keyValue3={OFFSET_APPLIED_SCOPES_COL}
            matched1="matchedVendor"
            matched2="matchedProjectType"
            matched3="matchedAppliedScopes"
            options1={getMergedVendors(uniqueValues, bulkMasterOptions.vendors)}
            options2={getMergedProjectTypes(uniqueValues, bulkMasterOptions.projectTypes)}
            options3={options}
            bulkUploadUrl="/scope4/off-set-credit-purchase/jsonupload"
            setsnackOpen={setsnackOpen}
            onConfirm={onConfirm}
          />
        )}
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default Offset;
